import React, { useState } from 'react';
import { GripVertical, ChevronDown, ChevronRight, Edit2, Trash2, Columns } from 'lucide-react';
import type { PageLayoutSection } from '../../../services/queries/layoutQueries';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Field Item Component (defined first so it can be used in LayoutSection)
interface FieldItemProps {
  field: { id: string; fieldId: string; fieldLabel: string; required: boolean };
  onRemove: () => void;
}

const SortableFieldItem: React.FC<FieldItemProps> = ({ field, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 group hover:border-purple-300 dark:hover:border-purple-600 transition-colors ${
        isDragging ? 'ring-2 ring-purple-500 shadow-lg' : ''
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none"
        style={{ touchAction: 'none' }}
      >
        <GripVertical className="w-4 h-4 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {field.fieldLabel}
          {field.required && <span className="text-red-600 dark:text-red-400 ml-1">*</span>}
        </div>
      </div>
      <button
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-all"
      >
        <Trash2 className="w-3 h-3 text-red-600 dark:text-red-400" />
      </button>
    </div>
  );
};

interface LayoutSectionProps {
  section: PageLayoutSection;
  onDrop: (sectionId: string, column: 0 | 1, index: number) => void;
  onDragOver: (sectionId: string, column: 0 | 1, index: number) => void;
  onRemoveField: (sectionId: string, fieldId: string) => void;
  onEditSection: (sectionId: string) => void;
  onSectionLabelChange: (sectionId: string, newLabel: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onToggleColumns: (sectionId: string) => void;
  onFieldReorder?: (sectionId: string, oldIndex: number, newIndex: number) => void;
  isDragging: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const LayoutSection: React.FC<LayoutSectionProps> = ({
  section,
  onDrop,
  onDragOver,
  onRemoveField,
  onEditSection,
  onSectionLabelChange,
  onDeleteSection,
  onToggleColumns,
  onFieldReorder,
  isDragging,
  dragHandleProps
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editLabelValue, setEditLabelValue] = useState(section.label);

  const commitLabelEdit = () => {
    const trimmed = editLabelValue.trim();
    if (trimmed) {
      onSectionLabelChange(section.id, trimmed);
    } else {
      setEditLabelValue(section.label);
    }
    setIsEditingLabel(false);
  };

  // Drag-and-drop sensors for field reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement to activate drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle field reordering within the section
  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !onFieldReorder) {
      return;
    }

    const oldIndex = section.fields.findIndex(f => f.id === active.id);
    const newIndex = section.fields.findIndex(f => f.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      onFieldReorder(section.id, oldIndex, newIndex);
    }
  };

  const handleDrop = (e: React.DragEvent, column: 0 | 1, index: number) => {
    e.preventDefault();
    // Try to extract field data from HTML5 dataTransfer as fallback
    const fieldJson = e.dataTransfer.getData('application/field');
    if (fieldJson) {
      try {
        const fieldData = JSON.parse(fieldJson);
        (onDrop as any)(section.id, column, index, fieldData);
        return;
      } catch {}
    }
    onDrop(section.id, column, index);
  };

  const handleDragOver = (e: React.DragEvent, column: 0 | 1, index: number) => {
    e.preventDefault();
    onDragOver(section.id, column, index);
  };

  const column1Fields = section.fields.filter((_, idx) => idx % 2 === 0);
  const column2Fields = section.columns === 2 ? section.fields.filter((_, idx) => idx % 2 === 1) : [];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Section Header */}
      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 flex-1">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <div
            {...(dragHandleProps || {})}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors touch-none"
            title="Drag to reorder section"
            style={{ touchAction: 'none' }}
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </div>
          {isEditingLabel ? (
            <input
              type="text"
              value={editLabelValue}
              onChange={(e) => setEditLabelValue(e.target.value)}
              onBlur={commitLabelEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitLabelEdit();
                if (e.key === 'Escape') {
                  setEditLabelValue(section.label);
                  setIsEditingLabel(false);
                }
              }}
              className="font-semibold text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-purple-400 dark:border-purple-500 rounded px-2 py-0.5 min-w-[120px] focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
              aria-label="Section name"
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setEditLabelValue(section.label);
                setIsEditingLabel(true);
              }}
              className="text-left font-semibold text-gray-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none focus:underline"
            >
              {section.label}
            </button>
          )}
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({section.fields.length} fields)
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onToggleColumns(section.id)}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title={section.columns === 1 ? 'Switch to 2 columns' : 'Switch to 1 column'}
          >
            <Columns className="w-4 h-4 text-gray-500" />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditLabelValue(section.label);
              setIsEditingLabel(true);
            }}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Edit section name"
            aria-label="Edit section name"
          >
            <Edit2 className="w-4 h-4 text-gray-500" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDeleteSection(section.id);
            }}
            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
            title="Delete section"
            aria-label="Delete section"
          >
            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
          </button>
        </div>
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleFieldDragEnd}
        >
          <div className={`p-4 grid gap-4 ${section.columns === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {/* Column 1 */}
            <SortableContext
              items={section.fields.map(f => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {column1Fields.map((field, index) => (
                  <SortableFieldItem
                    key={field.id}
                    field={field}
                    onRemove={() => onRemoveField(section.id, field.fieldId)}
                  />
                ))}
                <DropZone
                  onDrop={(e) => handleDrop(e, 0, column1Fields.length)}
                  onDragOver={(e) => handleDragOver(e, 0, column1Fields.length)}
                  isDragging={isDragging}
                  label={column1Fields.length === 0 ? 'Drop fields here' : 'Drop here to add'}
                />
              </div>
            </SortableContext>

            {/* Column 2 (if 2-column layout) */}
            {section.columns === 2 && (
              <SortableContext
                items={section.fields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {column2Fields.map((field, index) => (
                    <SortableFieldItem
                      key={field.id}
                      field={field}
                      onRemove={() => onRemoveField(section.id, field.fieldId)}
                    />
                  ))}
                  <DropZone
                    onDrop={(e) => handleDrop(e, 1, column2Fields.length)}
                    onDragOver={(e) => handleDragOver(e, 1, column2Fields.length)}
                    isDragging={isDragging}
                    label={column2Fields.length === 0 ? 'Drop fields here' : 'Drop here to add'}
                  />
                </div>
              </SortableContext>
            )}
          </div>
        </DndContext>
      )}
    </div>
  );
};

// Drop Zone Component
interface DropZoneProps {
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  isDragging: boolean;
  label: string;
}

const DropZone: React.FC<DropZoneProps> = ({ onDrop, onDragOver, isDragging, label }) => {
  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
        isDragging
          ? 'border-purple-400 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/10'
          : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
      }`}
    >
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
};
